import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, FileText, Trash2, GripVertical, Plus, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  container: {
    padding: '20px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  stats: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    height: '36px',
    paddingRight: '16px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
  },
  statsIcon: {
    background: 'var(--accent)',
    width: '44px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    marginRight: '12px',
    clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
  },
  statsText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: 'var(--text)',
    fontWeight: 700,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  card: (isDragging, isHovered) => ({
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '12px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '220px',
    cursor: 'grab',
    opacity: isDragging ? 0.4 : 1,
    transform: isHovered ? 'translateY(-4px)' : 'none',
    boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : 'none',
    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s, border-color 0.2s',
    zIndex: isHovered ? 20 : 1,
  }),
  thumbnail: {
    width: '100%',
    aspectRatio: '1 / 1.41',
    background: 'var(--surface2)',
    borderRadius: '6px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '1px solid var(--border)',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  pageInfo: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    textAlign: 'center',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  fileName: {
    fontWeight: 600,
    color: 'var(--text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actions: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '4px',
    zIndex: 10,
  },
  actionBtn: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-muted)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  dragHandle: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    color: 'var(--text-muted)',
    opacity: 0.5,
  },
  addCard: {
    background: 'rgba(59, 130, 246, 0.04)',
    border: '2px dashed var(--border-hi)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    minHeight: '220px',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '32px',
    padding: '16px',
    background: 'var(--surface2)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
  },
  fileItem: (color, isDragging) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    background: color,
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  }),
  fileLabel: {
    background: 'rgba(0,0,0,0.2)',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  }
};

const FILE_COLORS = [
  '#ef4444', // Red (A)
  '#3b82f6', // Blue (B)
  '#10b981', // Green (C)
  '#f59e0b', // Yellow (D)
  '#8b5cf6', // Purple (E)
  '#ec4899', // Pink (F)
];

const Thumbnail = ({ file, pageIndex, rotation }) => {
// ... existing Thumbnail component ...
  const [thumb, setThumb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setIsVisible(true); obs.disconnect(); }
    }, { rootMargin: '100px' });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !file) return;
    let active = true;
    const fetchThumb = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s for thumbnails
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('page', pageIndex);
        const res = await fetch(`${API_BASE}/render-pdf`, { 
            method: 'POST', 
            body: formData,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (res.ok && active) {
          const blob = await res.blob();
          setThumb(URL.createObjectURL(blob));
        }
      } catch (e) {
      } finally { if (active) setLoading(false); }
    };
    fetchThumb();
    return () => { active = false; if (thumb) URL.revokeObjectURL(thumb); };
  }, [file, pageIndex, isVisible]);

  return (
    <div ref={ref} style={{ ...styles.thumbnail, transform: `rotate(${rotation}deg)` }}>
      {loading ? <RotateCw className="animate-spin" size={20} color="var(--text-muted)" /> :
       thumb ? <img src={thumb} alt="page" style={styles.thumbImg} /> :
       <FileText size={32} color="var(--border-hi)" />}
    </div>
  );
};

export default function PDFOrganize({ files, onAddFiles, onRemoveFile, onReorderFiles, onSetSequence }) {
  const [sequence, setSequence] = useState([]);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [draggingFileIdx, setDraggingFileIdx] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTouch, setIsTouch] = useState(false);
  const [pageCounts, setPageCounts] = useState(new Map()); // fileIndex -> count
  const inputRef = useRef(null);

  useEffect(() => {
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Load page counts for any new files
  useEffect(() => {
    const loadNewCounts = async () => {
      let changed = false;
      const newCounts = new Map(pageCounts);
      setError(null);
      
      for (let i = 0; i < files.length; i++) {
        if (!newCounts.has(i)) {
          setIsLoading(true);
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout for large PDFs
            
            const formData = new FormData();
            formData.append('file', files[i].file);
            const res = await fetch(`${API_BASE}/convert/pages`, { 
                method: 'POST', 
                body: formData,
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (res.ok) {
              const data = await res.json();
              newCounts.set(i, data.totalPages);
              
              const newPages = [];
              for (let p = 0; p < data.totalPages; p++) {
                newPages.push({
                  fileIdx: i,
                  pageIdx: p,
                  id: `${i}-${p}-${Math.random().toString(36).substr(2, 9)}`,
                  rotation: 0
                });
              }
              setSequence(prev => [...prev, ...newPages]);
              changed = true;
            } else {
              throw new Error(`Failed to load pages for ${files[i].file.name}`);
            }
          } catch (e) {
            console.error("Error loading pages for file", i, e);
            if (e.name === 'AbortError') {
                setError("Connection timeout. Is the backend server running and accessible?");
            } else if (e.name === 'TypeError' && e.message.includes('fetch')) {
                setError(`Could not connect to backend at ${API_BASE} (${e.message}). Ensure the server is bound to 0.0.0.0`);
            } else {
                setError(`Error: ${e.message}`);
            }
          }
        }
      }
      
      if (changed) setPageCounts(newCounts);
      setIsLoading(false);
    };
    loadNewCounts();
  }, [files]);

  // Clean up sequence if a file is removed (shifts indices)
  useEffect(() => {
    setSequence(prev => {
        // If a file was removed, some fileIdx in sequence might now be out of bounds 
        // or pointing to the wrong file. This is complex to solve without stable IDs.
        // For now, let's just ensure we don't have invalid indices.
        return prev.filter(p => p.fileIdx < files.length);
    });
  }, [files.length]);

  useEffect(() => {
    const seqData = sequence.map(s => [s.fileIdx, s.pageIdx, s.rotation]);
    onSetSequence(JSON.stringify(seqData));
  }, [sequence, onSetSequence]);

  const handleRotate = (idx) => {
    setSequence(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], rotation: (next[idx].rotation + 90) % 360 };
      return next;
    });
  };

  const handleRemove = (idx) => {
    setSequence(prev => prev.filter((_, i) => i !== idx));
  };

  const handleMove = (idx, direction) => {
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= sequence.length) return;
    setSequence(prev => {
        const next = [...prev];
        const item = next[idx];
        next.splice(idx, 1);
        next.splice(nextIdx, 0, item);
        return next;
    });
  };

  const onDragStart = (e, index) => {
    setDraggingIdx(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, index) => {
    e.preventDefault();
    if (draggingIdx === null || draggingIdx === index) return;
    
    const nextSeq = [...sequence];
    const draggedItem = nextSeq[draggingIdx];
    nextSeq.splice(draggingIdx, 1);
    nextSeq.splice(index, 0, draggedItem);
    setDraggingIdx(index);
    setSequence(nextSeq);
  };

  const onDragEnd = () => {
    setDraggingIdx(null);
    setDraggingFileIdx(null);
  };

  const onFileDragStart = (e, index) => {
    setDraggingFileIdx(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onFileDragOver = (e, targetFileIdx) => {
    e.preventDefault();
    if (draggingFileIdx === null || draggingFileIdx === targetFileIdx) return;

    const fromIdx = draggingFileIdx;
    const toIdx = targetFileIdx;

    // 1. Tell parent to reorder the files array
    onReorderFiles(fromIdx, toIdx);

    // 2. Update sequence: Both indices and physical order
    setSequence(prev => {
        // First, update the fileIdx of all items to reflect the new files array
        const updatedIdxSeq = prev.map(p => {
            if (p.fileIdx === fromIdx) return { ...p, fileIdx: toIdx };
            if (fromIdx < toIdx) {
                if (p.fileIdx > fromIdx && p.fileIdx <= toIdx) return { ...p, fileIdx: p.fileIdx - 1 };
            } else {
                if (p.fileIdx >= toIdx && p.fileIdx < fromIdx) return { ...p, fileIdx: p.fileIdx + 1 };
            }
            return p;
        });

        // Second, reorder the items physically in the array so pages move as blocks
        const movedPages = updatedIdxSeq.filter(p => p.fileIdx === toIdx);
        const remainingPages = updatedIdxSeq.filter(p => p.fileIdx !== toIdx);
        
        // Find the insertion point (where the target index pages or their gap should be)
        // We want to insert 'movedPages' at the spot where the file at 'toIdx' previously was
        // or would be. A simple way is to find the first page that has an index >= toIdx.
        const insertAt = remainingPages.findIndex(p => p.fileIdx >= toIdx);
        if (insertAt === -1) {
            remainingPages.push(...movedPages);
            return remainingPages;
        } else {
            remainingPages.splice(insertAt, 0, ...movedPages);
            return remainingPages;
        }
    });

    setDraggingFileIdx(toIdx);
  };

  return (
    <div style={styles.container}>
      <input 
        type="file" multiple accept=".pdf" ref={inputRef} 
        style={{ display: 'none' }} 
        onChange={(e) => onAddFiles(Array.from(e.target.files))}
      />

      <div style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.statsIcon}>
            {isLoading ? <RotateCw className="animate-spin" size={18} strokeWidth={2.5} /> : <FileText size={18} strokeWidth={2.5} />}
          </div>
          <div style={styles.statsText}>
            <span>{isLoading ? 'Analyzing files...' : `${sequence.length} Pages Organized`}</span>
          </div>
        </div>
      </div>

      {files.length > 1 && (
        <div style={styles.fileList}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 600 }}>SWAP FILES (DRAG TO REORDER ALL PAGES)</div>
          {files.map((f, idx) => (
            <div 
              key={f.id}
              style={{
                  ...styles.fileItem(FILE_COLORS[f.colorIdx % FILE_COLORS.length], draggingFileIdx === idx),
                  cursor: isTouch ? 'default' : 'grab'
              }}
              draggable={!isTouch}
              onDragStart={(e) => !isTouch && onFileDragStart(e, idx)}
              onDragOver={(e) => !isTouch && onFileDragOver(e, idx)}
              onDragEnd={onDragEnd}
            >
              {!isTouch && <GripVertical size={16} />}
              <span style={styles.fileLabel}>{String.fromCharCode(65 + f.colorIdx)}</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
              
              {isTouch && (
                <div style={{ display: 'flex', gap: '4px' }}>
                    <div 
                        style={{ ...styles.actionBtn, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', opacity: idx === 0 ? 0.3 : 1 }}
                        onClick={() => idx > 0 && onReorderFiles(idx, idx - 1)}
                    >
                        <ChevronLeft size={14} style={{ transform: 'rotate(90deg)' }} />
                    </div>
                    <div 
                        style={{ ...styles.actionBtn, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', opacity: idx === files.length - 1 ? 0.3 : 1 }}
                        onClick={() => idx < files.length - 1 && onReorderFiles(idx, idx + 1)}
                    >
                        <ChevronRight size={14} style={{ transform: 'rotate(90deg)' }} />
                    </div>
                </div>
              )}

              <div 
                style={{ ...styles.actionBtn, background: 'transparent', border: 'none', color: '#fff', opacity: 0.8 }}
                onClick={(e) => { e.stopPropagation(); onRemoveFile(idx); }}
                title="Remove entire file"
              >
                <Trash2 size={14} />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <AlertCircle size={20} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>{error}</span>
        </div>
      )}

      {sequence.length === 0 && !isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          {error ? <p>Check your connection or try another file.</p> : <p>Please wait while we load your PDF pages...</p>}
        </div>
      ) : (
        <div style={styles.grid}>
          {sequence.map((item, idx) => (
            <div 
              key={item.id}
              style={{
                ...styles.card(draggingIdx === idx, hoveredIdx === idx),
                borderTop: `4px solid ${FILE_COLORS[(files[item.fileIdx]?.colorIdx ?? 0) % FILE_COLORS.length]}`,
                cursor: isTouch ? 'default' : 'grab'
              }}
              draggable={!isTouch}
              onDragStart={(e) => !isTouch && onDragStart(e, idx)}
              onDragOver={(e) => !isTouch && onDragOver(e, idx)}
              onDragEnd={onDragEnd}
              onMouseEnter={() => !isTouch && setHoveredIdx(idx)}
              onMouseLeave={() => !isTouch && setHoveredIdx(null)}
            >
              {!isTouch && <div style={styles.dragHandle}><GripVertical size={16} /></div>}
              <div style={styles.actions}>
                <div style={styles.actionBtn} onClick={() => handleRotate(idx)} title="Rotate"><RotateCw size={12} /></div>
                <div style={styles.actionBtn} onClick={() => handleRemove(idx)} title="Delete"><Trash2 size={12} /></div>
              </div>

              {isTouch && (
                <div style={{
                    position: 'absolute',
                    bottom: '50px',
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0 8px',
                    zIndex: 10
                }}>
                    <button 
                        style={{ ...styles.actionBtn, opacity: idx === 0 ? 0.3 : 1 }} 
                        onClick={() => handleMove(idx, -1)}
                        disabled={idx === 0}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button 
                        style={{ ...styles.actionBtn, opacity: idx === sequence.length - 1 ? 0.3 : 1 }} 
                        onClick={() => handleMove(idx, 1)}
                        disabled={idx === sequence.length - 1}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
              )}

              <Thumbnail 
                  file={files[item.fileIdx]?.file} 
                  pageIndex={item.pageIdx} 
                  rotation={item.rotation} 
              />
              <div style={styles.pageInfo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                    <span style={{ 
                        background: FILE_COLORS[files[item.fileIdx]?.colorIdx % FILE_COLORS.length], 
                        color: '#fff', 
                        padding: '0 4px', 
                        borderRadius: '3px',
                        fontSize: '9px'
                    }}>{String.fromCharCode(65 + (files[item.fileIdx]?.colorIdx ?? 0))}</span>
                    <span style={styles.fileName}>{files[item.fileIdx]?.file.name}</span>
                </div>
                <span>Page {item.pageIdx + 1}</span>
              </div>
            </div>
          ))}
          
          <div style={styles.addCard} onClick={() => inputRef.current?.click()}>
            <div style={{ ...styles.actionBtn, width: '32px', height: '32px', color: 'var(--accent)' }}>
              <Plus size={20} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>Add more files</span>
          </div>
        </div>
      )}
    </div>
  );
}
